// redux-store/slices/candidateKanbanSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosClient from "../../api/axiosClient";
import { decryptedAdminID } from "../../utility";
import { getKeySkills } from "../../apiServices";


/** Fetch registered candidates (/getNewCandidate) */
export const fetchRegisteredCandidates = createAsyncThunk(
  "candidateKanban/fetchRegistered",
  async (filters, { rejectWithValue }) => {
    try {
      const { data } = await axiosClient.post("/getNewCandidate", filters);
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Error");
    }
  }
);

/** Fetch candidate leads (/getCanLeads) */
export const fetchCanLeads = createAsyncThunk(
  "candidateKanban/fetchCanLeads",
  async (filters, { rejectWithValue }) => {
    try {
      const { data } = await axiosClient.post("/getNewCanLead", filters);
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Error");
    }
  }
);

export const fetchKeySkills = createAsyncThunk(
  "candidateKanban/fetchKeySkills",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getKeySkills();
      return response.results || [];  // ✅ FIXED
    } catch (err) {
      return rejectWithValue(err.response?.data || "Error");
    }
  }
);

// ─────────────────────────────────────────────
// DEFAULT FILTERS
// ─────────────────────────────────────────────



// ─── Role helpers (slice-level, read from localStorage) ───
const getSliceRole = () => {
  const roleID = decryptedAdminID();
  const isTeamLeader = localStorage.getItem("teamLeader") == "true";
  const isSuperAdmin = Number(roleID) === 1;
  return { isSuperAdmin, isTeamLeader };
};

const getDefaultAssign = () => {
  const { isSuperAdmin, isTeamLeader } = getSliceRole();
  const loggedId = Number(decryptedAdminID());

  return isSuperAdmin
    ? { assign_to: null, onlyMe: false, assign_to_type: "all" }
    : { assign_to: loggedId, onlyMe: true, assign_to_type: "self" };
};
// const getDefaultAssign = () => {
//   const { isSuperAdmin, isTeamLeader } = getSliceRole();
//   const loggedId = Number(decryptedAdminID());

//   // 🟢 SUPER ADMIN
//   if ((isSuperAdmin&&isTeamLeader)) {
//     return {
//       assign_to: null,
//       onlyMe: false,
//       assign_to_type: "all",
//     };
//   }

//   // 🟡 TEAM LEADER
//   if (isTeamLeader) {
//     return {
//       assign_to: loggedId,
//       onlyMe: true,
//       assign_to_type: "self",
//     };
//   }

//   // 🔵 ADMIN
//   return {
//     assign_to: loggedId,
//     onlyMe: true,
//     assign_to_type: "self",
//   };
// };

// Replace your static defaultCommonFilters assign fields:
const defaultCommonFilters = {
  mobile_number:  null,
  first_name:     null,
  job_category:   null,
  source:         null,
  skill:          null,
  pref_location:  null,
  pref_area:      null,
  exp_from:       null,
  exp_to:         null,
  ...getDefaultAssign(), // ✅ dynamic, reads fresh on slice init
};

const defaultRegisteredFilters = {
  current_stage_id:     null,
  current_sub_stage_id: null,
  sub_stage_date_from:  null,
  sub_stage_date_to:    null,
  created_from:         null,
  created_to:           null,
  entry_from:           null,
  entry_to:             null,
  from_source:          null,
  pref_location:        null,
  pref_area:            null,
  exp_from:             null,
  exp_to:               null,
  skill:                null,
  deActivated:          false,
  ...getDefaultAssign(), // ✅
};

const defaultCanLeadFilters = {
  current_stage_id:     null,
  current_sub_stage_id: null,
  sub_stage_date_from:  null,
  sub_stage_date_to:    null,
  created_from:         null,
  created_to:           null,
  exp_from:             null,
  exp_to:               null,
  fromSource:           null,
  pref_location:        null,
  qualification:        null,
  specification:        null,
  pref_area:            null,
  lead_deactive:        null,
  alreadyRegister:      null,
  registeredMoved:      null,
  ...getDefaultAssign(), // ✅
};
// ─────────────────────────────────────────────
// INITIAL STATE
// ─────────────────────────────────────────────

const initialState = {
  // ── Common filter (single filter panel drives both columns) ──
  commonFilters:   { ...defaultCommonFilters },
  commonHasFilter: false,

 keySkills: {
    data: [],
    loading: false,
    loaded: false,
    error: null,
  },
  // ── Registered Candidates ──
  registered: {
    data:       [],
    total:      0,
    page:       1,
    limit:      10,
    totalPages: 0,
    loading:    false,
    loaded: false,
    error:      null,
    filters:    { ...defaultCommonFilters, ...defaultRegisteredFilters },
    hasFilter:  false,
  },

  // ── Candidate Leads ──
  canLeads: {
    data:       [],
    total:      0,
    page:       1,
    limit:      10,
    totalPages: 0,
    loading:    false,
    loaded: false,
    error:      null,
    filters:    { ...defaultCommonFilters, ...defaultCanLeadFilters },
    hasFilter:  false,
  },

  // ── Shared ──

};

// ─────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────

/** True if any filter key has a real value */
const checkHasFilter = (filters, defaults) =>
  Object.keys(defaults).some((key) => {
    const val = filters[key];
    return val !== null && val !== undefined && val !== "";
  });

/**
 * Map common form → lead API keys
 * Export so the component can call before dispatch
 */
export const buildLeadFilters = (cf = {}) => ({
  mobile_number:       cf.mobile_number       ?? null,
  first_name:          cf.first_name          ?? null,
  job_category:        cf.job_category        ?? null,
  fromSource:          cf.source              ?? null, // ← renamed
  pref_location:       cf.pref_location       ?? null,
  pref_area:           cf.pref_area           ?? null,
  exp_from:            cf.exp_from            ?? null,
  exp_to:              cf.exp_to              ?? null,
  created_from:        cf.created_from        ?? null,
  created_to:          cf.created_to          ?? null,
  sub_stage_date_from: cf.sub_stage_date_from ?? null,
  sub_stage_date_to:   cf.sub_stage_date_to   ?? null,
  lead_deactive:       cf.lead_deactive       ?? null,
  alreadyRegister:     cf.alreadyRegister     ?? null,
  current_sub_stage_id:     cf.current_sub_stage_id     ?? null,
});

/**
 * Map common form → registered API keys
 * Export so the component can call before dispatch
 */
export const buildRegFilters = (cf = {}) => ({
  mobile_number:       cf.mobile_number       ?? null,
  first_name:          cf.first_name          ?? null,
  job_category:        cf.job_category        ?? null,
  from_source:         cf.source              ?? null, // ← renamed
  skill:               cf.skill               ?? null,
  pref_location:       cf.pref_location       ?? null,
  pref_area:           cf.pref_area           ?? null,
  exp_from:            cf.exp_from            ?? null,
  exp_to:              cf.exp_to              ?? null,
  created_from:        cf.created_from        ?? null,
  created_to:          cf.created_to          ?? null,
  entry_from:          cf.entry_from          ?? null,
  entry_to:            cf.entry_to            ?? null,
  sub_stage_date_from: cf.sub_stage_date_from ?? null,
  sub_stage_date_to:   cf.sub_stage_date_to   ?? null,
  deActivated:         cf.deActivated         ?? null,
  current_sub_stage_id:         cf.current_sub_stage_id         ?? null,
  qualification:  cf.qualification ??      null,
  specification:        cf.specification ??      null,
});

// ─────────────────────────────────────────────
// SLICE
// ─────────────────────────────────────────────

const candidateKanbanSlice = createSlice({
  name: "candidateKanban",
  initialState,
  reducers: {
    // ── Assign (shared) ─────────────────────
    setAssign(state, { payload }) {
      state.assign_to = payload.assign_to ?? null;
      state.onlyMe    = payload.onlyMe    ?? false;
    },

    // ── Common Filters (NEW) ─────────────────
    setCommonFilters(state, { payload }) {
      // state.commonFilters   = { ...defaultCommonFilters, ...payload };
      state.commonFilters = {
  ...defaultCommonFilters,
  ...state.commonFilters, // 👈 preserve existing
  ...payload,
};
      state.commonHasFilter = checkHasFilter(state.commonFilters, defaultCommonFilters);

      state.assign_to = payload.assign_to ?? state.assign_to;
      state.onlyMe    = payload.onlyMe ?? state.onlyMe;

      // Keep per-column filters in sync so legacy selectors still work
state.registered.filters = {
  ...state.registered.filters,
  ...buildRegFilters(state.commonFilters),
  assign_to: state.commonFilters.assign_to,
  onlyMe: state.commonFilters.onlyMe,
};
      state.registered.hasFilter = checkHasFilter(
        state.registered.filters,
        { ...defaultCommonFilters, ...defaultRegisteredFilters }
      );

state.canLeads.filters = {
  ...state.canLeads.filters,
  ...buildLeadFilters(state.commonFilters),
  assign_to: state.commonFilters.assign_to,
  onlyMe: state.commonFilters.onlyMe,
};
      state.canLeads.hasFilter = checkHasFilter(
        state.canLeads.filters,
        { ...defaultCommonFilters, ...defaultCanLeadFilters }
      );
  // state.registered.loaded = false;
  // state.canLeads.loaded = false;
      // Reset both to page 1
      state.registered.page = 1;
      state.canLeads.page   = 1;
    },

    resetCommonFilters(state) {
      state.commonFilters   = { ...defaultCommonFilters };
      state.commonHasFilter = false;

      state.registered.filters   = { ...defaultCommonFilters, ...defaultRegisteredFilters };
      state.registered.hasFilter = false;
      state.registered.page      = 1;

      state.canLeads.filters   = { ...defaultCommonFilters, ...defaultCanLeadFilters };
      state.canLeads.hasFilter = false;
      state.canLeads.page      = 1;
    },

    // ── Per-column filters (kept for backward compat) ──
    setRegisteredFilters(state, { payload }) {
      state.registered.filters = {
        ...state.registered.filters,
        ...payload,
      };
      state.registered.page      = 1;
      state.registered.hasFilter = checkHasFilter(
        state.registered.filters,
        { ...defaultCommonFilters, ...defaultRegisteredFilters }
      );
    },

    resetRegisteredFilters(state) {
      state.registered.filters   = { ...defaultCommonFilters, ...defaultRegisteredFilters };
      state.registered.page      = 1;
      state.registered.hasFilter = false;
    },

    setCanLeadFilters(state, { payload }) {
      state.canLeads.filters = {
        ...state.canLeads.filters,
        ...payload,
      };
      state.canLeads.page      = 1;
      state.canLeads.hasFilter = checkHasFilter(
        state.canLeads.filters,
        { ...defaultCommonFilters, ...defaultCanLeadFilters }
      );
    },

    resetCanLeadFilters(state) {
      state.canLeads.filters   = { ...defaultCommonFilters, ...defaultCanLeadFilters };
      state.canLeads.page      = 1;
      state.canLeads.hasFilter = false;
    },

    // ── Pagination ──────────────────────────
    setRegisteredPage(state, { payload }) {
      state.registered.page = payload;
    },

    setCanLeadPage(state, { payload }) {
      state.canLeads.page = payload;
    },
  },

  extraReducers: (builder) => {
    // ── Registered Candidates ──
    builder
      .addCase(fetchRegisteredCandidates.pending, (state) => {
        state.registered.loading = true;
        state.registered.error   = null;
      })
      .addCase(fetchRegisteredCandidates.fulfilled, (state, { payload }) => {
        state.registered.loading    = false;
        state.registered.loaded = true; 
        state.registered.data       = payload.data       || [];
        state.registered.total      = payload.total      || 0;
        state.registered.totalPages = payload.totalPages || 0;
        state.registered.limit      = payload.limit      || 10;
      })
      .addCase(fetchRegisteredCandidates.rejected, (state, { payload }) => {
        state.registered.loading = false;
        state.registered.error   = payload;
      });

    // ── Candidate Leads ──
    builder
      .addCase(fetchCanLeads.pending, (state) => {
        state.canLeads.loading = true;
        state.canLeads.error   = null;
      })
      .addCase(fetchCanLeads.fulfilled, (state, { payload }) => {
        state.canLeads.loading    = false;
        state.canLeads.loaded = true; 
        state.canLeads.data       = payload.data       || [];
        state.canLeads.total      = payload.total      || 0;
        state.canLeads.totalPages = payload.totalPages || 0;
        state.canLeads.limit      = payload.limit      || 10;
      })
      .addCase(fetchCanLeads.rejected, (state, { payload }) => {
        state.canLeads.loading = false;
        state.canLeads.error   = payload;
      });
builder
      .addCase(fetchKeySkills.pending, (state) => {
        state.keySkills.loading = true;
      })
      .addCase(fetchKeySkills.fulfilled, (state, action) => {
        state.keySkills.loading = false;
        state.keySkills.loaded = true;      // ✅ VERY IMPORTANT
        state.keySkills.data = action.payload;
      })
      .addCase(fetchKeySkills.rejected, (state, action) => {
        state.keySkills.loading = false;
        state.keySkills.error = action.payload;
      });
  },
});

// ─────────────────────────────────────────────
// EXPORTS — Actions
// ─────────────────────────────────────────────

export const {
  setAssign,
  setCommonFilters,
  resetCommonFilters,
  setRegisteredFilters,
  resetRegisteredFilters,
  setCanLeadFilters,
  resetCanLeadFilters,
  setRegisteredPage,
  setCanLeadPage,
} = candidateKanbanSlice.actions;

export default candidateKanbanSlice.reducer;

// ─────────────────────────────────────────────
// EXPORTS — Selectors
// ─────────────────────────────────────────────

export const selectRegistered      = (state) => state.candidateKanban.registered;
export const selectCanLeads        = (state) => state.candidateKanban.canLeads;
export const selectCommonFilters   = (state) => state.candidateKanban.commonFilters;
export const selectCommonHasFilter = (state) => state.candidateKanban.commonHasFilter;
export const selectKeySkills = (state) => state.candidateKanban.keySkills;
export const selectAssign          = (state) => ({
  assign_to: state.candidateKanban.assign_to,
  onlyMe:    state.candidateKanban.onlyMe,
});